'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ComponentType, JSX, SVGProps } from 'react';

import { Text, cn, getAccessibleFocusStyles } from '@videoblocks/design-system';
import { AiWand, GenerateImage, GenerateVideo, Waveform } from '@videoblocks/design-system/icons';

type SvgIconComponent = ComponentType<SVGProps<SVGSVGElement>>;

export const NAV_ITEMS: Array<{ href: string; label: string; icon: SvgIconComponent }> = [
    { href: '/ai/video-edit', label: 'Video Editor', icon: AiWand },
    { href: '/ai/video-generation', label: 'Video Generator', icon: GenerateVideo },
    { href: '/ai/image-generation', label: 'Image Generator', icon: GenerateImage },
    { href: '/ai/voiceover/text-to-speech', label: 'Voiceover', icon: Waveform },
];

const getSharedStyles = (isActive: boolean) =>
    cn(
        'rounded-sm text-center text-wrap transition-colors',
        'dark:text-neutral-dark-700 p-2',
        'dark:hover:bg-neutral-dark-200 dark:hover:text-neutral-dark-1000',
        isActive && 'dark:bg-neutral-dark-200 dark:text-neutral-dark-1000',
        getAccessibleFocusStyles(),
    );
/**
 * First/left most column in 3 column layout. Contains the shared navigation links for all AI features.
 */
export const AiToolkitSideNavBar = ({ isVideoEditEnabled }: { isVideoEditEnabled: boolean }): JSX.Element => {
    const navItems = isVideoEditEnabled ? NAV_ITEMS : NAV_ITEMS.filter((item) => item.href !== '/ai/video-edit');
    const pathname = usePathname();
    const isHomeActive = pathname === '/ai';
    const isItemActive = (item: { href: string }): boolean => {
        if (!item) return false;
        // remove the last part of the path that contains
        // the VO tab slug (e.g., 'text-to-speech') so
        // it can still match the pathname
        const itemPath = item.href.split('/')?.slice(0, 3).join('/');
        return pathname.startsWith(itemPath);
    };
    return (
        <nav className="flex flex-row md:flex-col w-full max-w-100vw md:max-w-24 shrink-0 overflow-y-auto gap-2 md:gap-6 px-2 py-2 md:py-5 border-b md:border-b-0 md:border-r dark:border-neutral-dark-200">
            <Text fontSize="sm" asChild className={cn('md:w-full h-19 md:h-auto', getSharedStyles(isHomeActive))}>
                <Link href="/ai" className="flex flex-col justify-center">
                    <h2>
                        AI
                        <br />
                        Toolkit
                    </h2>
                </Link>
            </Text>
            <div className="flex flex-row md:flex-col w-full gap-1.5 md:gap-3 text-center">
                {navItems.map((item) => (
                    <AiToolkitSideNavBarItem
                        key={item.href}
                        href={item.href}
                        label={item.label}
                        icon={item.icon}
                        isActive={isItemActive(item)}
                    />
                ))}
            </div>
        </nav>
    );
};

export type AiToolkitSideNavBarItemProps = {
    href: string;
    label: string;
    icon: SvgIconComponent;
    isActive?: boolean;
};

export const AiToolkitSideNavBarItem = ({
    href,
    label,
    icon: IconComponent,
    isActive = false,
}: AiToolkitSideNavBarItemProps): JSX.Element => {
    return (
        <Text
            asChild
            fontSize="xs"
            className={cn(
                'group flex flex-col shrink-0 w-18 md:w-full md:h-19 items-center justify-center gap-1',
                getSharedStyles(isActive),
            )}
        >
            <Link href={href}>
                <IconComponent
                    width={20}
                    height={20}
                    aria-hidden
                    className={cn(
                        'dark:[&_path]:fill-neutral-dark-700 dark:group-hover:[&_path]:fill-neutral-dark-1000',
                        isActive && 'dark:[&_path]:fill-neutral-dark-1000',
                    )}
                />
                {label}
                {/* <span className="hidden md:block">{label}</span> */}
            </Link>
        </Text>
    );
};
